import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Space, Modal, Form, Input, InputNumber, Switch, message, Tooltip, Select, Collapse } from 'antd';
import request from '../../../../utils/request';

const AdminWorkOrderRoutesList: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();
  const [insTplOptions, setInsTplOptions] = useState<Array<{ value:string; label:string }>>([]);
  const [insTplLoading, setInsTplLoading] = useState(false);
	const [helpVisible, setHelpVisible] = useState(false);
	const [helpTplVisible, setHelpTplVisible] = useState(false);
	const [helpPriorityVisible, setHelpPriorityVisible] = useState(false);
	const [helpCondVisible, setHelpCondVisible] = useState(false);
	const [helpPlanVisible, setHelpPlanVisible] = useState(false);
	const [helpOverridesVisible, setHelpOverridesVisible] = useState(false);
  // 高级设置与拆单/依赖开关
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [needSplitOrDeps, setNeedSplitOrDeps] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await request.get('/api/v1/workorders/workorder-routes/', { params: { ordering: 'instruction_template_code,priority' } });
      const data = resp?.data;
      const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      setRows(list);
    } catch (e:any) {
      message.error(e?.message || '加载失败');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const loadInstructionTemplates = async () => {
    if (insTplOptions.length > 0) return;
    setInsTplLoading(true);
    try {
      let list:any[] = [];
      // 优先尝试支持 all=1 的端点
      const endpoints = ['/api/v1/instruction/instruction-templates/', '/api/v1/instruction/templates/'];
      for (const ep of endpoints) {
        try {
          const resp = await request.get(ep, { params: { is_active: true, all: 1 } });
          const data = resp?.data;
          list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
          if (list && list.length) break;
        } catch {}
      }
      // 如未拿到，再用分页兜底
      if (!list || list.length === 0) {
        for (const ep of endpoints) {
          try {
            const pageSize = 200; let page = 1; const all:any[] = [];
            while (true) {
              const resp = await request.get(ep, { params: { page, page_size: pageSize, is_active: true } });
              const results = Array.isArray(resp?.data?.results) ? resp.data.results : [];
              if (!results || results.length === 0) break;
              all.push(...results);
              if (results.length < pageSize) break;
              page += 1; if (page > 1000) break;
            }
            if (all.length) { list = all; break; }
          } catch {}
        }
      }
      const opts = (list || []).map((t:any) => ({
        value: String(t.system_code || t.code || ''),
        label: `${t.name || '-'} · ${(t.system_code || t.code || '')}`
      })).filter(o => !!o.value);
      setInsTplOptions(opts);
    } finally { setInsTplLoading(false); }
  };

  const openNew = async () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ priority: 100, is_active: true, conditions: '{\n  \n}', plan: '[\n  {\n    "template_code": "RELABEL"\n  }\n]', overrides: '{\n  \n}' });
    setNeedSplitOrDeps(false);
    setAdvancedOpen(false);
    try { await loadInstructionTemplates(); } catch {}
    setModalOpen(true);
  };

  const openEdit = async (row: any) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      instruction_template_code: row.instruction_template_code,
      priority: row.priority,
      is_active: !!row.is_active,
      conditions: JSON.stringify(row.conditions || {}, null, 2),
      plan: JSON.stringify(row.plan || [], null, 2),
      overrides: JSON.stringify(row.overrides || {}, null, 2),
    });
    // 根据已有计划判断是否需要拆单/依赖
    try {
      const planArr = Array.isArray(row.plan) ? row.plan : [];
      const multi = planArr.length > 1;
      const hasAfter = planArr.some((p:any) => Array.isArray(p?.after) && p.after.length > 0);
      setNeedSplitOrDeps(multi || hasAfter);
      setAdvancedOpen(multi || hasAfter);
    } catch { setNeedSplitOrDeps(false); setAdvancedOpen(false); }
    try { await loadInstructionTemplates(); } catch {}
    setModalOpen(true);
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      const body:any = {
        instruction_template_code: String(v.instruction_template_code || '').trim(),
        priority: Number(v.priority || 100),
        is_active: !!v.is_active,
      };
      // 仅在启用“拆单/依赖”时保存条件/计划/覆盖；否则保存为空，走默认/模板
      if (needSplitOrDeps) {
        try { body.conditions = v.conditions ? JSON.parse(String(v.conditions)) : {}; } catch { body.conditions = {}; }
        try { body.plan = v.plan ? JSON.parse(String(v.plan)) : []; } catch { body.plan = []; }
        try { body.overrides = v.overrides ? JSON.parse(String(v.overrides)) : {}; } catch { body.overrides = {}; }
      } else {
        body.conditions = {};
        body.plan = [];
        body.overrides = {};
      }
      if (editing && editing.id) {
        await request.patch(`/api/v1/workorders/workorder-routes/${editing.id}/`, body);
        message.success('已更新');
      } else {
        await request.post('/api/v1/workorders/workorder-routes/', body);
        message.success('已创建');
      }
      setModalOpen(false);
      fetchData();
    } catch (e:any) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    }
  };

  const del = async (row:any) => {
    Modal.confirm({
      title: `删除路由 #${row.id}?`,
      onOk: async () => {
        try { await request.delete(`/api/v1/workorders/workorder-routes/${row.id}/`); message.success('已删除'); fetchData(); } catch { message.error('删除失败'); }
      }
    });
  };

  const toggleActive = async (row:any) => {
    try {
      await request.patch(`/api/v1/workorders/workorder-routes/${row.id}/`, { is_active: !row.is_active });
      fetchData();
    } catch { message.error('操作失败'); }
  };

  return (
    <div>
      <Card>
		<div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
          <div style={{ fontSize:16, fontWeight:600 }}>工单路由</div>
          <Space>
				<Button onClick={() => setHelpVisible(true)}>使用说明</Button>
            <Button onClick={fetchData}>刷新</Button>
            <Button type="primary" onClick={openNew}>新增路由</Button>
          </Space>
        </div>
        <Table
          size="small"
          rowKey={(r:any,i?:number)=> String(r?.id ?? i)}
          loading={loading}
          dataSource={rows}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          columns={[
            { title: '指令模板编码', dataIndex: 'instruction_template_code', width: 200 },
            { title: '优先级', dataIndex: 'priority', width: 90 },
            { title: '启用', dataIndex: 'is_active', width: 90, render: (v:any, r:any) => (
              <Switch checked={!!v} onChange={() => toggleActive(r)} />
            ) },
            { title: '条件(JSON)', dataIndex: 'conditions', render: (v:any) => (
              <Tooltip title={<pre style={{ margin:0 }}>{JSON.stringify(v||{}, null, 2)}</pre>}>
                <span style={{ color:'#1677ff', cursor:'pointer' }}>{JSON.stringify(v||{})?.slice(0,60) || '{}'}</span>
              </Tooltip>
            ) },
            { title: '计划(JSON)', dataIndex: 'plan', render: (v:any) => (
              <Tooltip title={<pre style={{ margin:0 }}>{JSON.stringify(v||[], null, 2)}</pre>}>
                <span style={{ color:'#1677ff', cursor:'pointer' }}>{JSON.stringify(v||[])?.slice(0,60) || '[]'}</span>
              </Tooltip>
            ) },
            { title: '操作', key: 'op', width: 160, render: (_:any, r:any) => (
              <Space>
                <Button size="small" onClick={() => openEdit(r)}>编辑</Button>
                <Button size="small" danger onClick={() => del(r)}>删除</Button>
              </Space>
            ) }
          ]}
        />
      </Card>

		<Modal
        title={editing ? `编辑路由 #${editing?.id}` : '新增路由'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={save}
        okText="保存"
        width={720}
      >
			<Form form={form} layout="vertical">
				<Form.Item
					label={(
						<div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
							<span>指令模板</span>
							<Button type="link" size="small" onClick={() => setHelpTplVisible(true)}>查看说明</Button>
						</div>
					)}
					name="instruction_template_code"
					rules={[{ required: true }]}
				>
            <Select
              allowClear
              showSearch
              placeholder="选择指令模板"
              options={insTplOptions}
              loading={insTplLoading}
              onFocus={() => loadInstructionTemplates()}
              filterOption={(input, option) => (option?.label as string).toLowerCase().includes(String(input).toLowerCase())}
            />
          </Form.Item>
				<Space size={12} style={{ display:'flex' }}>
					<Form.Item
						label={(
							<div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
								<span>优先级</span>
								<Button type="link" size="small" onClick={() => setHelpPriorityVisible(true)}>查看说明</Button>
							</div>
						)}
						name="priority"
						style={{ flex: 1 }}
					>
              <InputNumber style={{ width:'100%' }} />
            </Form.Item>
            <Form.Item label="启用" name="is_active" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
          </Space>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
          <Switch checked={needSplitOrDeps} onChange={(v)=> { setNeedSplitOrDeps(v); if (!v) setAdvancedOpen(false); }} />
          <span>需要拆单 / 依赖（启用后可配置计划与依赖/DAG）</span>
          <Button type="link" size="small" onClick={() => setAdvancedOpen((o)=>!o)}>{advancedOpen ? '收起高级设置' : '展开高级设置'}</Button>
        </div>
        {needSplitOrDeps && (
          <Collapse activeKey={advancedOpen ? ['adv'] : []} onChange={() => setAdvancedOpen(o => !o)}>
            <Collapse.Panel header="高级设置（条件 / 计划 / 覆盖）" key="adv">
              <Form.Item
                label={(
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span>条件(JSON)</span>
                    <Button type="link" size="small" onClick={() => setHelpCondVisible(true)}>查看示例</Button>
                  </div>
                )}
                name="conditions"
              >
                <Input.TextArea rows={4} placeholder='如 { "warehouse_id": 1 } 或 { "project_id": 128 }' />
              </Form.Item>
              <Form.Item
                label={(
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span>计划(JSON)</span>
                    <Button type="link" size="small" onClick={() => setHelpPlanVisible(true)}>查看示例</Button>
                  </div>
                )}
                name="plan"
                rules={needSplitOrDeps ? [{ required: true }] : []}
              >
                <Input.TextArea rows={8} placeholder='如 [ { "template_code": "RELABEL" }, { "template_code": "BOX_PACK", "after": ["RELABEL"] } ]' />
              </Form.Item>
              <Form.Item
                label={(
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span>覆盖(JSON)</span>
                    <Button type="link" size="small" onClick={() => setHelpOverridesVisible(true)}>查看示例</Button>
                  </div>
                )}
                name="overrides"
              >
                <Input.TextArea rows={4} placeholder='用于覆盖模板快照字段，通常留空 {}' />
              </Form.Item>
            </Collapse.Panel>
          </Collapse>
        )}
        </Form>
      </Modal>

    {/* 顶层使用说明 */}
    <Modal
      title="工单路由-使用说明"
      open={helpVisible}
      onCancel={() => setHelpVisible(false)}
      footer={<Button type="primary" onClick={() => setHelpVisible(false)}>我知道了</Button>}
      width={760}
    >
      <div style={{ lineHeight: 1.6 }}>
        <div>· 路由用于“将指令按条件映射到一组工单模板”，并可用依赖关系形成DAG执行顺序。</div>
        <div>· 核心字段：指令模板（限定适用场景）、条件（匹配范围）、计划（要生成的工单及依赖）、覆盖（实例化时的快照局部覆盖）。</div>
        <div>· 条件为空 {} 表示全量生效；如与其他路由重叠，系统会按 priority（数字越小越先匹配）选择优先路由。</div>
        <div>· 计划中的 after 使用模板编码（template_code）指向依赖来源。</div>
        <div style={{ height: 8 }} />
        <div>【产生时机】</div>
        <div>· 自动：在“自动生成工单模板与路由”（非预览）执行时，为对应的指令模板生成/更新路由；</div>
        <div>· 手动：可在本页或 Django Admin 新建/编辑；</div>
        <div>· 审核：审核通过不会自动新建/更新路由。</div>
        <div style={{ height: 8 }} />
        <div>【使用优先级】</div>
        <div>· 生成工单时优先级：审核计划覆盖 plan_override ＞ WorkOrderRoute（本页） ＞ 指令模板的默认工单模板（单工单）。</div>
        <div style={{ height: 8 }} />
        <div>【匹配规则】</div>
        <div>· 按指令的 template_snapshot.system_code 命中路由；当存在多条可用路由时，按 priority 升序选择第一条；is_active=false 的路由不参与匹配；</div>
        <div>· conditions 支持 warehouse_id / project_id（单值或数组）；为空 {} 代表全量；</div>
        <div>· plan 中 after 为依赖（可单值或数组），取值为工单模板编码（template_code）。</div>
        <div style={{ height: 8 }} />
        <div>【依赖/可视化】</div>
        <div>· 系统会将 after 写入 WorkOrderDependency（表），并把依赖ID放到 work_order.context.depends_on_wo_ids，供前端 DAG/可视化使用。</div>
        <div style={{ height: 8 }} />
        <div>【生效范围】</div>
        <div>· 路由修改仅影响之后受理的指令，不回溯已生成工单；仓内工单（internal）不走路由。</div>
      </div>
    </Modal>

    {/* 字段说明-指令模板 */}
    <Modal
      title="指令模板-说明"
      open={helpTplVisible}
      onCancel={() => setHelpTplVisible(false)}
      footer={null}
      width={700}
    >
      <div style={{ lineHeight: 1.6 }}>
        <div>· 选择与本路由绑定的“指令模板系统编码”。仅当指令属于该模板时，此路由才可能生效。</div>
        <div>· 列表为全量在用模板，展示“名称 · 系统编码”。提交值为模板系统编码。</div>
      </div>
    </Modal>

    {/* 字段说明-优先级 */}
    <Modal
      title="优先级-说明"
      open={helpPriorityVisible}
      onCancel={() => setHelpPriorityVisible(false)}
      footer={null}
      width={700}
    >
      <div style={{ lineHeight: 1.6 }}>
        <div>· 数字越小优先级越高。当多条路由同时匹配条件时，系统优先选择优先级更高的一条。</div>
        <div>· 建议：全量/广义路由设置较低优先级（数字大），细分/专项路由设置较高优先级（数字小）。</div>
      </div>
    </Modal>

    {/* 字段说明-条件 */}
    <Modal
      title="条件(JSON)-示例"
      open={helpCondVisible}
      onCancel={() => setHelpCondVisible(false)}
      footer={null}
      width={760}
    >
      <div style={{ marginBottom: 8 }}>等值与IN匹配，常用键：warehouse_id、project_id、customer_id、instruction_template_code、operation_type 等。</div>
      <pre style={{ background:'#f6f8fa', padding:12, borderRadius:4, margin:0 }}>{`{
  "warehouse_id": [1, 2],
  "customer_id": 1001,
  "project_id": [25101, 25102],
  "instruction_template_code": "RELABEL_SINGLE"
}`}</pre>
      <div style={{ height:8 }} />
      <div>仅按仓库：</div>
      <pre style={{ background:'#f6f8fa', padding:12, borderRadius:4, margin:0 }}>{`{ "warehouse_id": 1 }`}</pre>
      <div style={{ height:8 }} />
      <div>全量生效：</div>
      <pre style={{ background:'#f6f8fa', padding:12, borderRadius:4, margin:0 }}>{`{}`}</pre>
    </Modal>

    {/* 字段说明-计划 */}
    <Modal
      title="计划(JSON)-示例"
      open={helpPlanVisible}
      onCancel={() => setHelpPlanVisible(false)}
      footer={null}
      width={760}
    >
      <div style={{ marginBottom: 8 }}>数组元素为工单生成规则，template_code 为工单模板编码；after 指定依赖的前序工单（用模板编码）。</div>
      <div>线性（A → B）：</div>
      <pre style={{ background:'#f6f8fa', padding:12, borderRadius:4, margin:0 }}>{`[
  { "template_code": "RELABEL" },
  { "template_code": "BOX_PACK", "after": ["RELABEL"] }
]`}</pre>
      <div style={{ height:8 }} />
      <div>并行：</div>
      <pre style={{ background:'#f6f8fa', padding:12, borderRadius:4, margin:0 }}>{`[
  { "template_code": "RELABEL" },
  { "template_code": "QC_CHECK" }
]`}</pre>
      <div style={{ height:8 }} />
      <div>汇聚（A、B → C）：</div>
      <pre style={{ background:'#f6f8fa', padding:12, borderRadius:4, margin:0 }}>{`[
  { "template_code": "RELABEL" },
  { "template_code": "QC_CHECK" },
  { "template_code": "FINALIZE", "after": ["RELABEL", "QC_CHECK"] }
]`}</pre>
    </Modal>

    {/* 字段说明-覆盖 */}
    <Modal
      title="覆盖(JSON)-示例"
      open={helpOverridesVisible}
      onCancel={() => setHelpOverridesVisible(false)}
      footer={null}
      width={760}
    >
      <div style={{ marginBottom: 8 }}>对工单实例的“模板快照”进行局部覆盖，不影响模板定义本身。</div>
      <div>覆盖优先级/SLA/计费信息：</div>
      <pre style={{ background:'#f6f8fa', padding:12, borderRadius:4, margin:0 }}>{`{
  "meta": {
    "priority": 80,
    "sla_minutes": 120,
    "billing": { "unit_price": 0.2, "currency": "CNY" }
  }
}`}</pre>
      <div style={{ height:8 }} />
      <div>Checklist 默认值：</div>
      <pre style={{ background:'#f6f8fa', padding:12, borderRadius:4, margin:0 }}>{`{
  "checklist_defaults": { "print_label": true, "double_check": false }
}`}</pre>
    </Modal>
    </div>
  );
};

export default AdminWorkOrderRoutesList;


